import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Modal,
  ModalBody,
  Spinner,
  Table,
  UncontrolledDropdown,
} from 'reactstrap';

import StatusBadge from 'Components/pos2u/StatusBadge';
import {
  ServiceConnection,
  ServiceConnectionBank,
  ServiceConnectionFormValues,
  ServiceProviderDef,
  ServiceProviderKey,
  SERVICE_CONNECTION_LIMIT,
} from 'types/settings/serviceConnectionTypes';
import { SERVICE_PROVIDERS } from 'pages/SettingsV2/serviceConnectionConstants';
import {
  createServiceConnection,
  deleteServiceConnection,
  getServiceConnectionBanks,
  getServiceConnections,
  updateServiceConnectionStatus,
} from 'services/serviceConnectionService';
import ServiceConnectionModal from './ServiceConnectionModal';
import 'styles/common/hover-fill.css';

// Mask API Key for display (show first 4 and last 4 characters, hide the middle with asterisks)
const maskApiKey = (apiKey: string): string => {
  if (apiKey.length <= 10) return apiKey;
  return `${apiKey.slice(0, 4)}****${apiKey.slice(-4)}`;
};

const formatCreatedAt = (iso?: string): string => {
  if (!iso) return '-';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(
    date.getMonth() + 1
  )}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const SHOW_SECTION_SUBTITLE = false;

type PendingAction =
  | { type: 'toggle'; connection: ServiceConnection }
  | { type: 'delete'; connection: ServiceConnection }
  | null;

interface ServiceConnectionsSectionV2Props {
  providerAccess?: Partial<Record<ServiceProviderKey, boolean>>;
}

const getErrorMessage = (error: unknown): string | null => {
  if (error instanceof Error && error.message) return error.message;
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string' &&
    error.message
  ) {
    return error.message;
  }
  if (typeof error === 'string' && error) return error;
  return null;
};

const ServiceConnectionsSectionV2 = ({
  providerAccess,
}: ServiceConnectionsSectionV2Props) => {
  const { t } = useTranslation();

  const [connections, setConnections] = useState<ServiceConnection[]>([]);
  const [banks, setBanks] = useState<ServiceConnectionBank[]>([]);
  const [loading, setLoading] = useState(true);
  const [banksLoading, setBanksLoading] = useState(true);

  const [activeProviderKey, setActiveProviderKey] =
    useState<ServiceProviderKey | null>(null);

  const [formProvider, setFormProvider] = useState<ServiceProviderDef | null>(
    null
  );
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState<PendingAction>(null);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setBanksLoading(true);
    getServiceConnections()
      .then((data) => {
        if (!cancelled) setConnections(data);
      })
      .catch(() => {
        if (!cancelled) toast.error(t('Failed to load service connections'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    getServiceConnectionBanks()
      .then((data) => {
        if (!cancelled) setBanks(data);
      })
      .catch(() => {
        if (!cancelled) toast.error(t('Failed to load bank list'));
      })
      .finally(() => {
        if (!cancelled) setBanksLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [t]);

  const activeProvider = useMemo(
    () =>
      SERVICE_PROVIDERS.find((item) => item.key === activeProviderKey) ?? null,
    [activeProviderKey]
  );

  const isProviderAccessible = (providerKey: ServiceProviderKey) =>
    providerAccess?.[providerKey] ?? true;

  const countByProvider = useMemo(() => {
    const counts: Record<string, number> = {};
    connections.forEach((item) => {
      counts[item.provider] = (counts[item.provider] ?? 0) + 1;
    });
    return counts;
  }, [connections]);

  const providerConnections = useMemo(
    () =>
      activeProviderKey
        ? connections.filter((item) => item.provider === activeProviderKey)
        : [],
    [connections, activeProviderKey]
  );

  const reachedLimit = providerConnections.length >= SERVICE_CONNECTION_LIMIT;
  const sectionSubtitle = activeProvider
    ? t(activeProvider.subtitleKey)
    : t('Connect external services by registering their tokens');

  const openAddModal = () => {
    if (
      reachedLimit ||
      !activeProvider ||
      !isProviderAccessible(activeProvider.key)
    ) {
      return;
    }
    setFormProvider(activeProvider);
  };

  const handleCreate = async (values: ServiceConnectionFormValues) => {
    if (!activeProviderKey) return;
    setSaving(true);
    try {
      const created = await createServiceConnection(activeProviderKey, values);
      setConnections((prev) => [created, ...prev]);
      setFormProvider(null);
      toast.success(t('Service connection added'));
    } catch {
      toast.error(t('Failed to add service connection'));
    } finally {
      setSaving(false);
    }
  };

  const confirmPending = async () => {
    if (!pending) return;
    setProcessing(true);
    try {
      if (pending.type === 'toggle') {
        const nextActive = !pending.connection.isActive;
        const updated = await updateServiceConnectionStatus(
          pending.connection.id,
          nextActive
        );
        setConnections((prev) =>
          prev.map((item) => (item.id === updated.id ? updated : item))
        );
        toast.success(nextActive ? t('Key enabled') : t('Key disabled'));
      } else {
        await deleteServiceConnection(pending.connection.id);
        setConnections((prev) =>
          prev.filter((item) => item.id !== pending.connection.id)
        );
        toast.success(t('Key deleted'));
      }
      setPending(null);
    } catch (error) {
      if (pending.type === 'delete') {
        setPending(null);
      }
      const msg = getErrorMessage(error) || t('Action failed');
      toast.error(t(msg));
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Card className="shadow-none border mb-4">
      <CardHeader className="bg-transparent border-bottom">
        <div
          className={`d-flex gap-3 ${
            SHOW_SECTION_SUBTITLE ? 'align-items-start' : 'align-items-center'
          }`}
        >
          <div
            className="rounded-circle d-inline-flex align-items-center justify-content-center flex-shrink-0 bg-success-subtle text-success"
            style={{ width: 38, height: 38 }}
          >
            <i className="fs-5 ri-links-line" />
          </div>
          <div>
            <h5 className="card-title mb-0 fw-semibold d-flex align-items-center flex-wrap gap-1">
              {activeProvider ? (
                <>
                  <button
                    type="button"
                    className="hover-fill fw-semibold align-baseline"
                    onClick={() => setActiveProviderKey(null)}
                  >
                    {t('Service Connections')}
                  </button>
                  <i className="ri-arrow-right-s-line text-muted" />
                  <span>{activeProvider.name}</span>
                </>
              ) : (
                t('Service Connections')
              )}
            </h5>
            {SHOW_SECTION_SUBTITLE ? (
              <p className="mb-0 mt-1 fs-13 text-muted">{sectionSubtitle}</p>
            ) : null}
          </div>
        </div>
      </CardHeader>

      <CardBody>
        {!activeProvider ? (
          /* หน้ารวม: โชว์เฉพาะ icon ของแต่ละ channel ให้เลือก */
          <>
            <p className="text-muted mb-3">
              {t('Select a channel to connect')}
            </p>
            <div className="d-flex flex-wrap gap-3">
              {SERVICE_PROVIDERS.map((item) => {
                const count = countByProvider[item.key] ?? 0;
                const accessible = isProviderAccessible(item.key);
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => {
                      if (!accessible) return;
                      setActiveProviderKey(item.key);
                    }}
                    disabled={!accessible}
                    className={`btn border border-light rounded position-relative bg-white shadow-sm overflow-hidden ${
                      accessible ? '' : 'opacity-50'
                    }`}
                    style={{ width: 180 }}
                  >
                    {!accessible ? (
                      <span className="badge bg-warning-subtle text-warning position-absolute top-0 start-0 m-2">
                        {t('Access restricted')}
                      </span>
                    ) : null}
                    <span className="badge bg-primary-subtle text-primary position-absolute top-0 end-0 m-2">
                      {count}/{SERVICE_CONNECTION_LIMIT}
                    </span>
                    <div
                      className="d-flex flex-column align-items-center justify-content-center text-center px-3 py-4"
                      style={{ minHeight: 184 }}
                    >
                      <div
                        className="d-flex align-items-center justify-content-center mb-3"
                        style={{ minHeight: 64 }}
                      >
                        {item.logoSrc ? (
                          <img
                            src={item.logoSrc}
                            alt={item.name}
                            className="d-block mx-auto"
                            style={{
                              maxHeight: 56,
                              width: 'auto',
                              maxWidth: 132,
                            }}
                          />
                        ) : (
                          <i
                            className={`${item.icon} d-block text-primary`}
                            style={{ fontSize: 48 }}
                          />
                        )}
                      </div>
                      {/* {!item.logoSrc ? (
                        <span className="d-block fw-semibold fs-4 mb-1">
                          {item.name}
                        </span>
                      ) : null} */}
                      <span
                        className={`d-block ${
                          item.logoSrc ? 'fs-12' : 'fs-11'
                        } text-muted`}
                        style={{ lineHeight: 1.45 }}
                      >
                        {t(item.subtitleKey)}
                      </span>
                      {!accessible ? (
                        <span
                          className="d-block text-warning fs-11 mt-2"
                          style={{ lineHeight: 1.45 }}
                        >
                          {t(
                            "Your account or plan doesn't support this provider"
                          )}
                        </span>
                      ) : null}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        ) : (
          <>
            <p className="text-muted mb-1">
              {t(activeProvider.descriptionKey)}
            </p>
            {activeProvider.registerUrl ? (
              <a
                href={activeProvider.registerUrl}
                target="_blank"
                rel="noreferrer"
                className="link-primary"
              >
                {t('Sign up for {{name}}', { name: activeProvider.name })}
              </a>
            ) : null}

            <div className="d-flex align-items-center justify-content-end gap-3 mt-4 mb-2">
              <span
                className={`fs-13 ${reachedLimit ? 'text-danger' : 'text-muted'}`}
              >
                {t('Limit')} {providerConnections.length}/
                {SERVICE_CONNECTION_LIMIT}
              </span>
              <Button
                color="primary"
                onClick={openAddModal}
                disabled={reachedLimit}
                className="d-inline-flex align-items-center gap-1"
              >
                <i className="ri-add-line" />
                {t('Add Connection')}
              </Button>
            </div>

            {/* token table */}
            <div className="table-responsive">
              <Table className="align-middle table-nowrap mb-0">
                <thead className="table-light">
                  <tr>
                    <th scope="col">{t('Status')}</th>
                    <th scope="col">{t('Merchant ID')}</th>
                    <th scope="col">{t('Key Name')}</th>
                    <th scope="col">{t('API Key')}</th>
                    <th scope="col">{t('Payment Channel')}</th>
                    <th scope="col">{t('Created Date')}</th>
                    <th scope="col" className="text-end">
                      {t('Actions')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4">
                        <Spinner color="primary" size="sm" />
                      </td>
                    </tr>
                  ) : providerConnections.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        {t('No service connections yet')}
                      </td>
                    </tr>
                  ) : (
                    providerConnections.map((conn) => (
                      <tr key={conn.id}>
                        <td>
                          <StatusBadge
                            statusStyle={
                              conn.isActive ? 'success' : 'secondary'
                            }
                            statusText={
                              conn.isActive ? t('Active') : t('Disabled')
                            }
                          />
                        </td>
                        <td>{conn.merchantId}</td>
                        <td>{conn.keyName}</td>
                        <td>
                          <code className="text-body">
                            {maskApiKey(conn.apiKey)}
                          </code>
                        </td>
                        <td>
                          {conn.bankAccountNumber
                            ? `${conn.bankAccountNumber} / ${conn.bankAccountName}`
                            : '-'}
                        </td>
                        <td>{formatCreatedAt(conn.createdAt)}</td>
                        <td className="text-end">
                          <UncontrolledDropdown>
                            <DropdownToggle
                              tag="button"
                              type="button"
                              className="btn btn-light btn-sm"
                            >
                              <i className="ri-more-2-fill" />
                            </DropdownToggle>
                            <DropdownMenu end container="body">
                              <DropdownItem
                                onClick={() =>
                                  setPending({
                                    type: 'toggle',
                                    connection: conn,
                                  })
                                }
                              >
                                <i
                                  className={`me-2 align-middle ${
                                    conn.isActive
                                      ? 'ri-pause-circle-line'
                                      : 'ri-play-circle-line'
                                  }`}
                                />
                                {conn.isActive ? t('Disable') : t('Enable')}
                              </DropdownItem>
                              <DropdownItem
                                className="text-danger"
                                onClick={() =>
                                  setPending({
                                    type: 'delete',
                                    connection: conn,
                                  })
                                }
                              >
                                <i className="ri-delete-bin-line me-2 align-middle" />
                                {t('Delete')}
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledDropdown>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          </>
        )}
      </CardBody>

      {/* Modal form for adding connections */}
      <ServiceConnectionModal
        isOpen={!!formProvider}
        provider={formProvider}
        banks={banks}
        banksLoading={banksLoading}
        saving={saving}
        onClose={() => setFormProvider(null)}
        onSubmit={handleCreate}
      />

      {/* Modal for deleting */}
      <Modal
        isOpen={!!pending}
        toggle={() => (processing ? undefined : setPending(null))}
        centered
      >
        <ModalBody className="py-4 px-4 text-center">
          <i
            className={`display-5 ${
              pending?.type === 'delete'
                ? 'ri-delete-bin-line text-danger'
                : 'ri-error-warning-line text-warning'
            }`}
          />
          <div className="mt-3 fs-15">
            <h4 className="mb-2">
              {pending?.type === 'delete'
                ? t('Delete this key?')
                : pending?.connection.isActive
                  ? t('Disable this key?')
                  : t('Enable this key?')}
            </h4>
            <p className="text-muted mb-0">
              {pending?.type === 'delete'
                ? t(
                    'This action cannot be undone. We recommend disabling and testing the key first.'
                  )
                : pending?.connection.isActive
                  ? t(
                      'Disabling this key will stop it from calling the API. You can enable it again anytime.'
                    )
                  : t('Enabling this key will allow it to call the API again.')}
            </p>
          </div>
          <div className="d-flex gap-2 justify-content-center mt-4">
            <Button
              color="light"
              className="w-sm"
              onClick={() => setPending(null)}
              disabled={processing}
            >
              {t('Cancel')}
            </Button>
            <Button
              color={
                pending?.type === 'delete'
                  ? 'danger'
                  : pending?.connection.isActive
                    ? 'danger'
                    : 'primary'
              }
              className="w-sm d-inline-flex align-items-center justify-content-center gap-1"
              onClick={confirmPending}
              disabled={processing}
            >
              {processing ? <Spinner size="sm" /> : null}
              {pending?.type === 'delete'
                ? t('Delete')
                : pending?.connection.isActive
                  ? t('Disable')
                  : t('Enable')}
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </Card>
  );
};

export default ServiceConnectionsSectionV2;
